import React, { useState, useEffect, useCallback } from 'react';
import { Search, AlertCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listMetrics } from '../../services/adminService';
import { getMetricItems } from '../../services/metricsService';
import LoadingSpinner from '../common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const MetricsList = () => {
  const navigate = useNavigate();
  const [tempSearchQuery, setTempSearchQuery] = useState('');
  const [metricsResult, setMetricsResult] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;
  const [error, setError] = useState(null);
  const [segmentsModalOpen, setSegmentsModalOpen] = useState(false);
  const [segmentsModalLoading, setSegmentsModalLoading] = useState(false);
  const [segmentsModalItems, setSegmentsModalItems] = useState([]);
  const [segmentsModalMetricId, setSegmentsModalMetricId] = useState('');

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listMetrics({
        pageSize: pageSize,
        pageNumber: currentPage - 1
      });
      const metrics = response.result || [];
      setMetricsResult(metrics);
      const derivedTotal = response.totalCount ?? response.total ?? response.count ?? 0;
      const derivedPageCount = response.pageCount
        || (derivedTotal ? Math.ceil(derivedTotal / pageSize) : (metrics.length === pageSize ? currentPage + 1 : currentPage));
      setPageCount(derivedPageCount);

      setSegmentsModalItems([]);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      setError('Failed to load metrics');
      setMetricsResult([]);
      setPageCount(1);
      setSegmentsModalItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(Number(timestamp.seconds) * 1000);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const calculateCompletionTime = (started, finished) => {
    if (!started || !finished) return '-';
    const startTime = Number(started.seconds) * 1000;
    const finishTime = Number(finished.seconds) * 1000;
    const diffInSeconds = (finishTime - startTime) / 1000;
    
    if (diffInSeconds < 0) return '-';
    
    if (diffInSeconds < 60) {
      return `${Math.round(diffInSeconds)}s`;
    } else if (diffInSeconds < 3600) {
      return `${Math.round(diffInSeconds / 60)}m`;
    } else {
      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.round((diffInSeconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const handleRefresh = () => {
    fetchMetrics();
  };

  const handleExploreSegments = async (metric) => {
    const metricId = metric?.metricId || metric?.id;
    if (!metricId) return;
    setSegmentsModalMetricId(metricId);
    setSegmentsModalOpen(true);
    setSegmentsModalLoading(true);
    try {
      const itemsResponse = await getMetricItems(metricId, 100, 0);
      setSegmentsModalItems(itemsResponse?.items || []);
    } catch (error) {
      console.error('Failed to load metric items:', error);
      setSegmentsModalItems([]);
    } finally {
      setSegmentsModalLoading(false);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pageCount, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    buttons.push(
      <button
        key="prev"
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-md bg-white text-[#020817] hover:bg-[#F1F5F9] disabled:text-[#9CA3AF] disabled:bg-[#F9FAFB]"
      >
        ← Prev
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1.5 text-sm rounded-md border ${
            i === currentPage
              ? 'bg-[#2563EB] text-white border-[#2563EB]'
              : 'bg-white text-gray-600 border-[#E2E8F0] hover:bg-[#F1F5F9]'
          }`}
        >
          {i}
        </button>
      );
    }

    if (currentPage < pageCount) {
      buttons.push(
        <button
          key="next"
          onClick={() => setCurrentPage(currentPage + 1)}
          className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-md bg-white text-[#020817] hover:bg-[#F1F5F9]"
        >
          Next →
        </button>
      );
    }

    return buttons;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-red-500 mb-4">
            <AlertCircle size={48} />
          </div>
          <h3 className="text-lg font-semibold mb-2">Session Expired</h3>
          <p className="text-gray-600 mb-4">Your session has expired. Please sign in again.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
          >
            Go to Login
          </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-8">
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search metrics..."
            value={tempSearchQuery}
            onChange={(e) => setTempSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          title="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.1)]">
        <div style={{ 
          padding: '0px 0px 1rem',
          marginBottom: '1rem'
        }}>
          <div className="overflow-x-scroll" style={{ 
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: '-ms-autohiding-scrollbar'
          }}>
            <style>
              {`
                .scroll-container::-webkit-scrollbar {
                  -webkit-appearance: none;
                  height: 4px;
                }
                .scroll-container::-webkit-scrollbar-track {
                  background-color: rgba(0, 0, 0, .05);
                }
                .scroll-container::-webkit-scrollbar-thumb {
                  border-radius: 2px;
                  background-color: rgba(0, 0, 0, .2);
                }
              `}
            </style>
            <div className="scroll-container rounded-lg overflow-hidden shadow" style={{ overflowX: 'scroll', paddingBottom: '1rem' }}>
              <table className="min-w-[1280px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px]">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[160px]">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Created</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Started</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Finished</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Completion</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Segments</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Metrics</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="px-4 pt-6 py-4 text-center">
                        <LoadingSpinner size="medium" />
                      </td>
                    </tr>
                  ) : metricsResult.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-4 pt-6 py-4 text-center text-sm text-gray-500">
                        No results found
                      </td>
                    </tr>
                  ) : (
                    metricsResult.map((metric) => {
                      return (
                        <tr key={metric.id} className="hover:bg-gray-50">
                         <td className="px-4 py-2 whitespace-nowrap text-sm font-medium overflow-hidden text-ellipsis">
                           <button
                             onClick={() => window.open(`/metrics/${metric.id}`, '_blank')}
                             className="hover:text-[#020817] cursor-pointer"
                           >
                             {metric.id}
                           </button>
                         </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {metric.name || '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {metric.email || '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                              {metric.status || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(metric.created)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(metric.started)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(metric.completed)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 text-center">
                            {calculateCompletionTime(metric.started, metric.completed)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-600 whitespace-normal">
                            <button
                              onClick={() => handleExploreSegments(metric)}
                              className="text-[#2563EB] hover:text-[#1D4ED8] font-medium"
                            >
                              Explore
                            </button>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 break-words whitespace-normal">
                            {Array.isArray(metric.metrics) 
                              ? metric.metrics.join(', ')
                              : typeof metric.metrics === 'string'
                              ? metric.metrics
                              : typeof metric.metrics === 'object'
                              ? Object.keys(metric.metrics || {}).join(', ')
                              : '-'
                            }
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        {renderPaginationButtons()}
      </div>

      <Dialog open={segmentsModalOpen} onOpenChange={setSegmentsModalOpen}>
        <DialogContent className="max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Segments for {segmentsModalMetricId}</DialogTitle>
          </DialogHeader>
          {segmentsModalLoading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <LoadingSpinner size="medium" />
            </div>
          ) : segmentsModalItems.length === 0 ? (
            <div className="text-sm text-gray-500">No segments found.</div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto space-y-3">
              {segmentsModalItems.map((item, index) => (
                <div key={item.id || index} className="rounded-lg border border-gray-200 p-3">
                  <div className="text-xs text-gray-400 mb-2">#{index + 1}</div>
                  <div className="space-y-2 text-sm text-gray-800">
                    <div><span className="font-medium text-[#020817]">Source:</span> {item.sourceText || '—'}</div>
                    <div><span className="font-medium text-[#020817]">Target:</span> {item.targetText || '—'}</div>
                    <div><span className="font-medium text-[#020817]">Reference:</span> {item.referenceText || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminPage = () => {
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Metrics Admin</h1>
      </div>
      <MetricsList />
    </div>
  );
};

export default AdminPage;
